import { makeAutoObservable } from 'mobx';
import { RootStore } from './RootStore';

export interface User {
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    role: 'ADMIN' | 'DRIVER' | 'USER';
    phone_number: string;
    profile_picture_url?: string;
}

export interface Driver {
    id: number;
    user: User;
    license_number: string;
    license_expiry: string;
    status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
    vehicle?: number;
    created_at?: string;
    updated_at?: string;
}

export interface DriverFormData {
    user: {
        username: string;
        email: string;
        first_name: string;
        last_name: string;
        phone_number: string;
        profile_picture_url?: string;
    };
    license_number: string;
    license_expiry: string;
    status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
    vehicle?: number;
}

export class DriverStore {
    drivers: Driver[] = [];
    isLoading = false;
    isSubmitting = false;
    error: string | null = null;

    constructor(private rootStore: RootStore) {
        makeAutoObservable(this);
    }

    // API Base URL
    private get apiUrl() {
        return process.env.REACT_APP_API_URL || 'http://localhost:8000';
    }

    // Get authorization header
    private getAuthHeaders() {
        const token = this.rootStore.authStore.token;
        return {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
        };
    }

    // Load all drivers
    async loadDrivers() {
        this.isLoading = true;
        this.error = null;

        try {
            const response = await fetch(`${this.apiUrl}/api/drivers/`, {
                method: 'GET',
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            this.drivers = data.results || data;
        } catch (error) {
            console.error('Error loading drivers:', error);
            this.error = error instanceof Error ? error.message : 'Error al cargar conductores';
        } finally {
            this.isLoading = false;
        }
    }

    // Get single driver
    async getDriver(id: number): Promise<Driver | null> {
        this.error = null;

        try {
            const response = await fetch(`${this.apiUrl}/api/drivers/${id}/`, {
                method: 'GET',
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error getting driver:', error);
            this.error = error instanceof Error ? error.message : 'Error al obtener conductor';
            return null;
        }
    }

    // Create driver
    async createDriver(driverData: DriverFormData): Promise<boolean> {
        this.isSubmitting = true;
        this.error = null;

        try {
            const response = await fetch(`${this.apiUrl}/api/drivers/`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify(driverData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(this.formatErrorMessage(errorData));
            }

            const newDriver = await response.json();
            this.drivers.push(newDriver);
            return true;
        } catch (error) {
            console.error('Error creating driver:', error);
            this.error = error instanceof Error ? error.message : 'Error al crear conductor';
            return false;
        } finally {
            this.isSubmitting = false;
        }
    }

    // Update driver
    async updateDriver(id: number, driverData: Partial<DriverFormData>): Promise<boolean> {
        this.isSubmitting = true;
        this.error = null;

        try {
            const response = await fetch(`${this.apiUrl}/api/drivers/${id}/`, {
                method: 'PUT',
                headers: this.getAuthHeaders(),
                body: JSON.stringify(driverData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(this.formatErrorMessage(errorData));
            }

            const updatedDriver = await response.json();
            const index = this.drivers.findIndex(driver => driver.id === id);
            if (index !== -1) {
                this.drivers[index] = updatedDriver;
            }
            return true;
        } catch (error) {
            console.error('Error updating driver:', error);
            this.error = error instanceof Error ? error.message : 'Error al actualizar conductor';
            return false;
        } finally {
            this.isSubmitting = false;
        }
    }

    // Delete driver
    async deleteDriver(id: number): Promise<boolean> {
        this.isSubmitting = true;
        this.error = null;

        try {
            const response = await fetch(`${this.apiUrl}/api/drivers/${id}/`, {
                method: 'DELETE',
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }

            this.drivers = this.drivers.filter(driver => driver.id !== id);
            return true;
        } catch (error) {
            console.error('Error deleting driver:', error);
            this.error = error instanceof Error ? error.message : 'Error al eliminar conductor';
            return false;
        } finally {
            this.isSubmitting = false;
        }
    }

    // Validation for driver form
    validateDriverForm(data: DriverFormData) {
        const errors: Record<string, string> = {};

        // User validation
        if (!data.user.first_name?.trim()) {
            errors.first_name = 'El nombre es requerido';
        }
        if (!data.user.last_name?.trim()) {
            errors.last_name = 'El apellido es requerido';
        }
        if (!data.user.email?.trim()) {
            errors.email = 'El email es requerido';
        } else if (!/\S+@\S+\.\S+/.test(data.user.email)) {
            errors.email = 'El email no es válido';
        }
        if (!data.user.username?.trim()) {
            errors.username = 'El nombre de usuario es requerido';
        }
        if (!data.user.phone_number?.trim()) {
            errors.phone_number = 'El teléfono es requerido';
        }

        // Driver-specific validation
        if (!data.license_number?.trim()) {
            errors.license_number = 'El número de licencia es requerido';
        }
        if (!data.license_expiry) {
            errors.license_expiry = 'La fecha de vencimiento de licencia es requerida';
        } else {
            const expiryDate = new Date(data.license_expiry);
            const today = new Date();
            if (expiryDate <= today) {
                errors.license_expiry = 'La fecha de vencimiento debe ser futura';
            }
        }
        if (!data.status) {
            errors.status = 'El estado es requerido';
        }
        if (!data.vehicle) {
            errors.vehicle = 'Debe seleccionar un vehículo para el conductor';
        }

        return {
            isValid: Object.keys(errors).length === 0,
            errors
        };
    }

    // Format error messages from API
    private formatErrorMessage(errorData: any): string {
        if (typeof errorData === 'string') {
            return errorData;
        }

        if (errorData.detail) {
            return errorData.detail;
        }

        if (errorData.non_field_errors) {
            return errorData.non_field_errors.join(', ');
        }

        // Handle field-specific errors
        const fieldErrors = Object.entries(errorData)
            .filter(([key, value]) => Array.isArray(value))
            .map(([key, value]) => `${key}: ${(value as string[]).join(', ')}`)
            .join('\n');

        return fieldErrors || 'Error en el servidor';
    }

    // Clear error
    clearError() {
        this.error = null;
    }

    // Get driver display name
    getDriverDisplayName(driver: Driver): string {
        return `${driver.user.first_name} ${driver.user.last_name}`.trim();
    }

    // Get driver status label
    getStatusLabel(status: string): string {
        switch (status) {
            case 'ACTIVE':
                return 'Activo';
            case 'INACTIVE':
                return 'Inactivo';
            case 'SUSPENDED':
                return 'Suspendido';
            default:
                return status;
        }
    }

    // Get driver status color for UI
    getStatusColor(status: string): 'success' | 'warning' | 'error' | 'default' {
        switch (status) {
            case 'ACTIVE':
                return 'success';
            case 'INACTIVE':
                return 'warning';
            case 'SUSPENDED':
                return 'error';
            default:
                return 'default';
        }
    }
}
